import androidx.compose.runtime.*
import kotlinx.browser.document
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLInputElement
import kotlinx.coroutines.launch

class DuelState {
    var homeScore by mutableStateOf("")
    var awayScore by mutableStateOf("")
    var homePlayed by mutableStateOf(true)
    var awayPlayed by mutableStateOf(true)
}

/** Null until both sides are "ready" (marked not-played, or a score was actually typed in). */
private fun duelPointsOrNull(d: DuelState): Pair<Int, Int>? {
    val homeReady = !d.homePlayed || d.homeScore.isNotBlank()
    val awayReady = !d.awayPlayed || d.awayScore.isNotBlank()
    if (!homeReady || !awayReady) return null

    // Both sides skipped: no one actually played, so no one earns the duel point either.
    if (!d.homePlayed && !d.awayPlayed) return 0 to 0

    val h = if (d.homePlayed) d.homeScore.toIntOrNull() ?: 0 else 0
    val a = if (d.awayPlayed) d.awayScore.toIntOrNull() ?: 0 else 0
    return when {
        h > a -> 1 to 0
        a > h -> 0 to 1
        else -> 1 to 1
    }
}

private fun phaseLabel(phase: String): String = if (phase == "autumn") "Podzim" else "Jaro"

/** Keeps only digits and caps length at 3 chars; range 0-300 is flagged as invalid rather than rewritten. */
private fun sanitizeScoreInput(raw: String): String = raw.filter { it.isDigit() }.take(3)

private fun isValidScore(raw: String): Boolean = raw.isBlank() || (raw.toIntOrNull()?.let { it in 0..300 } == true)

/** Mirrors the paper scoresheet: the losing side shows a dash, only the winner shows a number. */
private fun pointsCellText(pts: Int?): String = if (pts == null || pts == 0) "—" else pts.toString()

/** Tab order is duel-by-duel, home then away; null once past the very last input. */
private fun nextScoreInputId(duelIndex: Int, side: String): String? = when (side) {
    "home" -> "score-$duelIndex-away"
    else -> if (duelIndex < 8) "score-${duelIndex + 1}-home" else null
}

private fun focusScoreInput(id: String?) {
    if (id == null) return
    (document.getElementById(id) as? HTMLElement)?.focus()
}

@Composable
fun MatchResultScreen(
    matchId: String,
    homeTeamId: String,
    homeTeam: String,
    awayTeamId: String,
    awayTeam: String,
    seasonId: String,
    leagueName: String,
    phase: String,
    roundNumber: Int,
    token: String?,
    role: String?,
    onResultSaved: suspend () -> Unit,
    onDone: () -> Unit,
) {
    val duels = remember(matchId) { List(9) { DuelState() } }
    var homeRoster by remember { mutableStateOf<List<PlayerDto>>(emptyList()) }
    var awayRoster by remember { mutableStateOf<List<PlayerDto>>(emptyList()) }
    var lineupA by remember { mutableStateOf("") }
    var lineupB by remember { mutableStateOf("") }
    var lineupC by remember { mutableStateOf("") }
    var lineupX by remember { mutableStateOf("") }
    var lineupY by remember { mutableStateOf("") }
    var lineupZ by remember { mutableStateOf("") }
    var playedAt by remember { mutableStateOf("") }
    var loading by remember { mutableStateOf(true) }
    var submitting by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    var showForfeitPanel by remember { mutableStateOf(false) }
    var forfeitResult by remember { mutableStateOf<String?>(null) }
    var confirmCancelForfeit by remember(matchId) { mutableStateOf(false) }
    var protocolImagePath by remember(matchId) { mutableStateOf<String?>(null) }
    var uploadingPhoto by remember { mutableStateOf(false) }
    var photoError by remember { mutableStateOf<String?>(null) }
    val scope = rememberCoroutineScope()

    LaunchedEffect(matchId) {
        loading = true
        try {
            homeRoster = Api.fetchRoster(token, homeTeamId, seasonId).mapNotNull { it.player }
            awayRoster = Api.fetchRoster(token, awayTeamId, seasonId).mapNotNull { it.player }

            val matchRow = Api.fetchMatch(token, matchId)
            playedAt = matchRow.playedAt ?: ""
            forfeitResult = if (matchRow.isForfeit) matchRow.result else null
            protocolImagePath = matchRow.protocolImagePath

            Api.fetchMatchLineup(token, matchId).forEach { entry ->
                when (entry.position) {
                    "A" -> lineupA = entry.playerId
                    "B" -> lineupB = entry.playerId
                    "C" -> lineupC = entry.playerId
                    "X" -> lineupX = entry.playerId
                    "Y" -> lineupY = entry.playerId
                    "Z" -> lineupZ = entry.playerId
                }
            }

            Api.fetchMatchDuels(token, matchId).forEachIndexed { i, d ->
                if (i < duels.size) {
                    duels[i].homePlayed = d.homePlayed
                    duels[i].awayPlayed = d.awayPlayed
                    duels[i].homeScore = d.homeScore?.toString() ?: ""
                    duels[i].awayScore = d.awayScore?.toString() ?: ""
                }
            }

            error = null
        } catch (e: Throwable) {
            error = "Načtení zápasu selhalo: ${e.apiMessage()}"
        }
        loading = false
    }

    val lineupComplete = listOf(lineupA, lineupB, lineupC, lineupX, lineupY, lineupZ).all { it.isNotBlank() }
    fun nameFor(playerId: String, roster: List<PlayerDto>): String = roster.find { it.id == playerId }?.name ?: "?"

    Div(attrs = { classes("card") }) {
        H2 { Text("Zápis o utkání") }
        Button(attrs = { onClick { onDone() } }) { Text("Zpět na rozpis") }

        Div(attrs = { classes("match-paper-header") }) {
            Div(attrs = { classes("match-paper-row") }) {
                Span(attrs = { classes("match-paper-label") }) { Text("Pořádající:") }
                B { Text(homeTeam) }
            }
            Div(attrs = { classes("match-paper-row") }) {
                Span(attrs = { classes("match-paper-label") }) { Text("Hostující:") }
                B { Text(awayTeam) }
            }
            Div(attrs = { classes("match-paper-row") }) {
                Span(attrs = { classes("match-paper-label") }) { Text("Liga:") }
                Text("$leagueName · ${phaseLabel(phase)} — kolo $roundNumber")
            }
        }

        forfeitResult?.let { r ->
            val (loser, winner) = if (r == "home_forfeit") homeTeam to awayTeam else awayTeam to homeTeam
            Div(attrs = { classes("editor-error") }) {
                Text("Tento zápas byl kontumován — prohrává $loser, vítězí $winner 9:0.")
            }
        }

        if (!role.canScore()) {
            Div { Text("Pro zadání výsledku se musíš přihlásit jako scorer/admin/superadmin.") }
        } else if (loading) {
            Text("Načítám soupisky…")
        } else {
            Div(attrs = { classes("actions") }) {
                Label { Text("Datum zápasu: ") }
                Input(type = InputType.Date) {
                    value(playedAt)
                    onInput { playedAt = it.value }
                }
                if (role.canManage()) {
                    Label {
                        CheckboxInput(checked = showForfeitPanel) {
                            onInput { input -> showForfeitPanel = input.value }
                        }
                        Text(" Kontumace")
                    }
                }
            }

            if (forfeitResult != null) {
                if (role.canManage()) {
                    Div(attrs = { classes("actions") }) {
                        Label {
                            CheckboxInput(checked = confirmCancelForfeit) {
                                onInput { input -> confirmCancelForfeit = input.value }
                            }
                            Text(" Zrušit kontumaci a zadat normální zápis (sestava a jednotlivé duely)")
                        }
                    }
                } else {
                    Div { Text("Kontumaci může zrušit pouze administrátor.") }
                }
            }

            if (showForfeitPanel) {
                Div(attrs = { classes("card") }) {
                    H3 { Text("Kontumace") }
                    P { Text("Pokud se zápas neuskutečnil (např. družstvo se nedostavilo), zapiš rovnou kontumaci — bez sestavy a jednotlivých duelů.") }
                    Div(attrs = { classes("actions") }) {
                        Button(attrs = {
                            if (submitting) disabled()
                            onClick {
                                submitting = true
                                error = null
                                scope.launch {
                                    try {
                                        Api.submitForfeit(token, matchId, "home", playedAt.ifBlank { null })
                                        onResultSaved()
                                        submitting = false
                                        onDone()
                                    } catch (e: Throwable) {
                                        error = "Kontumace selhala: ${e.apiMessage()}"
                                        submitting = false
                                    }
                                }
                            }
                        }) { Text("Kontumace — prohrává $homeTeam") }
                        Button(attrs = {
                            if (submitting) disabled()
                            onClick {
                                submitting = true
                                error = null
                                scope.launch {
                                    try {
                                        Api.submitForfeit(token, matchId, "away", playedAt.ifBlank { null })
                                        onResultSaved()
                                        submitting = false
                                        onDone()
                                    } catch (e: Throwable) {
                                        error = "Kontumace selhala: ${e.apiMessage()}"
                                        submitting = false
                                    }
                                }
                            }
                        }) { Text("Kontumace — prohrává $awayTeam") }
                    }
                }
            }

            if (forfeitResult == null || confirmCancelForfeit) {
            Div(attrs = { classes("lineup-columns") }) {
                Div(attrs = { classes("lineup-column") }) {
                    H3 { Text("Sestava — $homeTeam") }
                    PlayerSelect("A", homeRoster.filter { it.id !in listOf(lineupB, lineupC) }, lineupA) { lineupA = it }
                    PlayerSelect("B", homeRoster.filter { it.id !in listOf(lineupA, lineupC) }, lineupB) { lineupB = it }
                    PlayerSelect("C", homeRoster.filter { it.id !in listOf(lineupA, lineupB) }, lineupC) { lineupC = it }
                }
                Div(attrs = { classes("lineup-column") }) {
                    H3 { Text("Sestava — $awayTeam") }
                    PlayerSelect("X", awayRoster.filter { it.id !in listOf(lineupY, lineupZ) }, lineupX) { lineupX = it }
                    PlayerSelect("Y", awayRoster.filter { it.id !in listOf(lineupX, lineupZ) }, lineupY) { lineupY = it }
                    PlayerSelect("Z", awayRoster.filter { it.id !in listOf(lineupX, lineupY) }, lineupZ) { lineupZ = it }
                }
            }

            if (!lineupComplete) {
                Div { Text("Nejdřív vyber sestavu obou týmů (A, B, C, X, Y, Z) — pak se zobrazí duely.") }
            } else {
                val lineupByPosition = mapOf(
                    "A" to nameFor(lineupA, homeRoster), "B" to nameFor(lineupB, homeRoster), "C" to nameFor(lineupC, homeRoster),
                    "X" to nameFor(lineupX, awayRoster), "Y" to nameFor(lineupY, awayRoster), "Z" to nameFor(lineupZ, awayRoster),
                )

                H3 { Text("Jednotlivé výsledky") }
                Table(attrs = { classes("paper-style-table") }) {
                    Tr {
                        Th { Text("Utkání") }
                        Th { Text("Hráči pořádajícího družstva") }
                        Th { Text("Hráči hostujícího družstva") }
                        Th { Text("Výsledek hry") }
                        Th(attrs = { attr("colspan", "2") }) { Text("Body") }
                    }
                    DUEL_SEQUENCE.forEachIndexed { i, (homePos, awayPos) ->
                        val duel = duels[i]
                        val points = duelPointsOrNull(duel)
                        Tr {
                            Td(attrs = { attr("data-label", "Duel") }) { Text("${i + 1}.") }
                            Td(attrs = { attr("data-label", "Domácí") }) {
                                Div(attrs = { classes("duel-name-cell") }) {
                                    CheckboxInput(checked = duel.homePlayed) {
                                        tabIndex(-1)
                                        attr("title", "Odškrtni, pokud hráč tento duel nehrál")
                                        onInput { duel.homePlayed = it.value }
                                    }
                                    Text(lineupByPosition[homePos] ?: homePos)
                                }
                            }
                            Td(attrs = { attr("data-label", "Hosté") }) {
                                Div(attrs = { classes("duel-name-cell") }) {
                                    CheckboxInput(checked = duel.awayPlayed) {
                                        tabIndex(-1)
                                        attr("title", "Odškrtni, pokud hráč tento duel nehrál")
                                        onInput { duel.awayPlayed = it.value }
                                    }
                                    Text(lineupByPosition[awayPos] ?: awayPos)
                                }
                            }
                            Td(attrs = { attr("data-label", "Skóre") }) {
                                Div(attrs = { classes("duel-result-cell") }) {
                                    Input(type = InputType.Text) {
                                        classes("score-input")
                                        if (!isValidScore(duel.homeScore)) classes("score-input-invalid")
                                        attr("maxlength", "3")
                                        attr("inputmode", "numeric")
                                        attr("title", "Platný rozsah je 0-300")
                                        attr("id", "score-$i-home")
                                        value(duel.homeScore)
                                        onInput {
                                            val sanitized = sanitizeScoreInput(it.value)
                                            duel.homeScore = sanitized
                                            if (sanitized.length == 3) focusScoreInput(nextScoreInputId(i, "home"))
                                        }
                                    }
                                    Span(attrs = { classes("duel-result-sep") }) { Text(":") }
                                    Input(type = InputType.Text) {
                                        classes("score-input")
                                        if (!isValidScore(duel.awayScore)) classes("score-input-invalid")
                                        attr("maxlength", "3")
                                        attr("inputmode", "numeric")
                                        attr("title", "Platný rozsah je 0-300")
                                        attr("id", "score-$i-away")
                                        value(duel.awayScore)
                                        onInput {
                                            val sanitized = sanitizeScoreInput(it.value)
                                            duel.awayScore = sanitized
                                            if (sanitized.length == 3) focusScoreInput(nextScoreInputId(i, "away"))
                                        }
                                    }
                                }
                            }
                            Td(attrs = { classes("duel-points-cell") }) { Text(pointsCellText(points?.first)) }
                            Td(attrs = { classes("duel-points-cell") }) { Text(pointsCellText(points?.second)) }
                        }
                    }
                }

                val computedPoints = duels.map { duelPointsOrNull(it) }
                val totalHomePts = computedPoints.sumOf { it?.first ?: 0 }
                val totalAwayPts = computedPoints.sumOf { it?.second ?: 0 }
                val totalHomeScore = duels.sumOf { if (it.homePlayed) it.homeScore.toIntOrNull() ?: 0 else 0 }
                val totalAwayScore = duels.sumOf { if (it.awayPlayed) it.awayScore.toIntOrNull() ?: 0 else 0 }
                Div(attrs = { classes("match-summary") }) {
                    Div(attrs = { classes("summary-row") }) { Text("Celkový výsledek — bodů: $totalHomePts : $totalAwayPts") }
                    Div(attrs = { classes("summary-row") }) { Text("Kuželky domácí — hosté: $totalHomeScore : $totalAwayScore") }
                }

                val allDuelsComplete = duels.all { duelPointsOrNull(it) != null }
                val allScoresValid = duels.all { isValidScore(it.homeScore) && isValidScore(it.awayScore) }
                val formComplete = playedAt.isNotBlank() && allDuelsComplete && allScoresValid

                ProtocolPhotoSection(
                    matchId = matchId,
                    token = token,
                    path = protocolImagePath,
                    uploading = uploadingPhoto,
                    error = photoError,
                    onUploadStart = { uploadingPhoto = true; photoError = null },
                    onUploaded = { path -> protocolImagePath = path; uploadingPhoto = false },
                    onError = { msg -> photoError = msg; uploadingPhoto = false },
                    onRemove = { protocolImagePath = null },
                )

                Div(attrs = { classes("save-action") }) {
                    if (!formComplete) {
                        Div {
                            Text(
                                when {
                                    !allScoresValid -> "Některé skóre je mimo platný rozsah 0-300 (zvýrazněno červeně)."
                                    playedAt.isBlank() -> "Pro uložení nejdřív vyplň datum zápasu a skóre všech odehraných duelů."
                                    else -> "Pro uložení vyplň skóre všech odehraných duelů (nebo je odškrtni jako nehrané)."
                                }
                            )
                        }
                    }

                    Button(attrs = {
                        if (submitting || !formComplete) disabled()
                        onClick {
                            submitting = true
                            error = null
                            scope.launch {
                                try {
                                    val duelInputs = duels.map {
                                        DuelInput(
                                            homePlayed = it.homePlayed,
                                            awayPlayed = it.awayPlayed,
                                            homeScore = if (it.homePlayed) it.homeScore.toIntOrNull() else null,
                                            awayScore = if (it.awayPlayed) it.awayScore.toIntOrNull() else null,
                                        )
                                    }
                                    val lineup = LineupInput(lineupA, lineupB, lineupC, lineupX, lineupY, lineupZ)
                                    Api.submitMatchResult(token, matchId, duelInputs, lineup, playedAt.ifBlank { null }, protocolImagePath)
                                    onResultSaved()
                                    submitting = false
                                    onDone()
                                } catch (e: Throwable) {
                                    error = "Uložení selhalo: ${e.apiMessage()}"
                                    submitting = false
                                }
                            }
                        }
                    }) { Text(if (submitting) "Ukládám…" else "Uložit výsledek") }
                }
            }
            }

            error?.let { Div { Text("Chyba: $it") } }
        }
    }
}

/** Optional photo/scan of the paper scoresheet (spec 10.9) — resizes client-side, uploads to the
 * `match-protocols` bucket under a fixed `$matchId.jpg` path (so re-upload just overwrites), and
 * hands the resulting path back to the caller to submit alongside the rest of the match result. */
@Composable
private fun ProtocolPhotoSection(
    matchId: String,
    token: String?,
    path: String?,
    uploading: Boolean,
    error: String?,
    onUploadStart: () -> Unit,
    onUploaded: (String) -> Unit,
    onError: (String) -> Unit,
    onRemove: () -> Unit,
) {
    val fileInputId = "protocol-photo-input"
    val scope = rememberCoroutineScope()

    Div(attrs = { classes("card", "protocol-photo-section") }) {
        H3 { Text("Foto zápisu (volitelné)") }
        path?.let { p ->
            Div(attrs = { classes("protocol-photo-preview") }) {
                Img(src = Api.matchProtocolImageUrl(p), alt = "Náhled fotky zápisu", attrs = {
                    classes("protocol-photo-thumb")
                    onClick { openLightbox(Api.matchProtocolImageUrl(p)) }
                })
                Button(attrs = { onClick { onRemove() } }) { Text("Odebrat fotku") }
            }
        }
        Div(attrs = { classes("actions") }) {
            Button(attrs = {
                if (uploading) disabled()
                attr("type", "button")
                onClick { document.getElementById(fileInputId)?.asDynamic()?.click() }
            }) { Text(if (uploading) "Nahrávám…" else if (path != null) "Nahrát jinou fotku" else "Nahrát fotku") }
            Input(type = InputType.File, attrs = {
                attr("id", fileInputId)
                attr("accept", "image/*")
                style { display(DisplayStyle.None) }
                onChange { e ->
                    val input = e.target as HTMLInputElement
                    val file = input.files?.item(0) ?: return@onChange
                    onUploadStart()
                    scope.launch {
                        try {
                            val resized = resizeImageForUpload(file)
                            val uploadedPath = Api.uploadMatchProtocolImage(token, matchId, resized)
                            onUploaded(uploadedPath)
                        } catch (ex: Throwable) {
                            onError("Nahrání fotky selhalo: ${ex.apiMessage()}")
                        } finally {
                            input.value = ""
                        }
                    }
                }
            })
        }
        error?.let { Div { Text("Chyba: $it") } }
    }
}

@Composable
private fun PlayerSelect(label: String, roster: List<PlayerDto>, selected: String, onSelect: (String) -> Unit) {
    Div {
        Span { Text("$label: ") }
        Select(attrs = { onChange { onSelect(it.value ?: "") } }) {
            Option("", attrs = { if (selected == "") selected() }) { Text("— vyber —") }
            roster.forEach { player ->
                Option(player.id, attrs = { if (player.id == selected) selected() }) { Text(player.name) }
            }
        }
    }
}
